import { useCallback, useContext, useEffect, useMemo } from "react";
import { PathBuilderContext } from "@src/components/libraryItemDetailPages/module/contexts/PathBuilderContext";
import { SetActiveClientInput } from "@src/types.generated";
import { ClientIdContext } from "@builder/shared/ClientIdContext";
import { CourseBuilderContext } from "@src/components/libraryItemDetailPages/course/contexts/CourseBuilderContext";
import { SkillBuilderContext } from "@builder/skill/contexts/SkillBuilderContext";
import { debounce, isEqual } from "lodash";
import {
  useRemoveActiveClientMutation,
  useSetActiveClientMutation,
} from "../build/CourseBuildContent.generated";
import { useAtomValue } from "jotai";
import { pathVersionIdAtom } from "../../module/atoms";
import { courseVersionIdAtom, undefinedAtom } from "../atoms";
import { selectAtom } from "jotai/utils";
import { richTextDocumentIdAtom } from "../../resource/atoms";
type Opts = { skip?: boolean };

const useSyncActiveClient = (opts?: Opts): void => {
  const { skip } = opts || {};
  const courseBuilderContext = useContext(CourseBuilderContext);
  const pathBuilderContext = useContext(PathBuilderContext);
  const skillBuilderContext = useContext(SkillBuilderContext);
  const clientIdContext = useContext(ClientIdContext);
  const [removeActiveClientMutation] = useRemoveActiveClientMutation();
  const [setActiveClientMutation] = useSetActiveClientMutation({
    ignoreResults: true,
  });
  const removeActiveClient = useCallback(() => {
    if (!clientIdContext) return;
    removeActiveClientMutation({
      variables: { clientId: clientIdContext.clientId },
    });
  }, [clientIdContext, removeActiveClientMutation]);
  const promptUuidAtom = useMemo(
    () =>
      selectAtom(
        courseBuilderContext?.activeScreenAtom ?? undefinedAtom,
        (activeScreen) => {
          if (activeScreen && "promptScreen" in activeScreen) {
            return activeScreen.promptScreen.prompt.uuid;
          }
          return null;
        },
        isEqual,
      ),
    [courseBuilderContext?.activeScreenAtom],
  );
  const promptUuid = useAtomValue(promptUuidAtom);
  const courseVersionId = useAtomValue(courseVersionIdAtom);
  const pathVersionId = useAtomValue(pathVersionIdAtom);
  const richTextDocumentId = useAtomValue(richTextDocumentIdAtom);
  const input: SetActiveClientInput | null = useMemo(() => {
    if (!clientIdContext) return null;
    return {
      clientId: clientIdContext.clientId,
      promptUuid,
      courseVersionId: courseVersionId || null,
      pathVersionId: pathVersionId || null,
      pathContentMembershipUuid:
        pathBuilderContext?.activeMembershipUuid || null,
      skillVersionId: skillBuilderContext?.skillVersion?.id || null,
      verificationStepId: skillBuilderContext?.activeStepId,
      richTextDocumentId: richTextDocumentId,
    } satisfies SetActiveClientInput;
  }, [
    clientIdContext,
    promptUuid,
    courseVersionId,
    pathVersionId,
    pathBuilderContext?.activeMembershipUuid,
    skillBuilderContext?.skillVersion?.id,
    skillBuilderContext?.activeStepId,
    richTextDocumentId,
  ]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSetActiveClient = useCallback(
    debounce(() => {
      if (!input) {
        return;
      }
      setActiveClientMutation({
        variables: { input },
      });
    }, 1000),
    [input, setActiveClientMutation],
  );

  useEffect(() => {
    if (skip) return;
    debouncedSetActiveClient();

    return () => debouncedSetActiveClient.cancel();
  }, [input, debouncedSetActiveClient, skip]);

  useEffect(() => {
    if (skip) return;
    const interval = setInterval(() => {
      debouncedSetActiveClient();
    }, 1000 * 10);

    return () => {
      clearInterval(interval);
      debouncedSetActiveClient.cancel();
    };
  }, [debouncedSetActiveClient, skip]);

  useEffect(() => {
    if (skip) return;
    const unload = () => {
      removeActiveClient();
    };
    window.addEventListener("beforeunload", unload);
    return () => {
      unload();
      window.removeEventListener("beforeunload", unload);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
};

export default useSyncActiveClient;
